import {
  Deferred,
  MethodInfo,
  NextServerStreamingFn,
  NextUnaryFn,
  RpcInterceptor,
  RpcMetadata,
  RpcOptions,
  RpcOutputStreamController,
  RpcStatus,
  ServerStreamingCall,
  UnaryCall,
} from '@protobuf-ts/runtime-rpc'

import {
  ActiveLegacySession,
  ActiveSession,
  ClientSession,
  EmptySession,
} from 'shared/utils/auth/v2/utils'
import { isK2Error, isSystemError, isUserError } from 'shared/utils/errors'
import { makeErrorFromRpcError } from 'shared/utils/errors/rpc.utils'

import logger from '../logger'

type SessionTuple = [
  ClientSession | ActiveSession | EmptySession | null,
  ActiveLegacySession | EmptySession | null | undefined,
]
type SingleSession = ClientSession | ActiveSession | EmptySession | null

type SessionCallback = () => Promise<SessionTuple | SingleSession>

/**
 * Undeprecating this, because we _should_ be letting the transport handle the bearer token fetching at the time of the request
 * - This is the recommended way to handle authentication in gRPC
 */
export class BearerInterceptor implements RpcInterceptor {
  constructor(private callback: SessionCallback) {}

  interceptUnary(
    next: NextUnaryFn,
    method: MethodInfo,
    input: object,
    options: RpcOptions,
  ): UnaryCall {
    const defHeader = new Deferred<RpcMetadata>()
    const defMessage = new Deferred<object>()
    const defStatus = new Deferred<RpcStatus>()
    const defTrailer = new Deferred<RpcMetadata>()

    void (async () => {
      try {
        let session: ActiveSession | EmptySession | ClientSession | null
        try {
          const result = await this.callback()
          if (Array.isArray(result)) {
            ;[session] = result
          } else {
            session = result
          }
        } catch (sessionError) {
          let bearerError: Error

          if (isUserError(sessionError) || isSystemError(sessionError)) {
            bearerError = sessionError
          } else if (isK2Error(sessionError)) {
            bearerError = sessionError.toApplicationError()
          } else {
            bearerError = makeErrorFromRpcError(sessionError)
          }

          // Reject all deferreds
          defHeader.rejectPending(bearerError)
          defMessage.rejectPending(bearerError)
          defStatus.rejectPending(bearerError)
          defTrailer.rejectPending(bearerError)
          return
        }

        // Add authorization header if we have a token
        if (!options.meta) options.meta = {}
        if (session?.accessToken) {
          options.meta.Authorization = `Bearer ${session.accessToken}`
        }

        // Make the actual gRPC call - let errors from here propagate naturally
        const call = next(method, input, options)
        const result = await call

        // Resolve all deferreds with the result
        defHeader.resolve(result.headers)
        defMessage.resolve(result.response)
        defStatus.resolve(result.status)
        defTrailer.resolve(result.trailers)
      } catch (callError) {
        // Error from the actual gRPC call - pass through unchanged
        defHeader.rejectPending(callError)
        defMessage.rejectPending(callError)
        defStatus.rejectPending(callError)
        defTrailer.rejectPending(callError)
      }
    })()

    return new UnaryCall<object, object>(
      method,
      options.meta ?? {},
      input,
      defHeader.promise,
      defMessage.promise,
      defStatus.promise,
      defTrailer.promise,
    )
  }

  interceptServerStreaming(
    next: NextServerStreamingFn,
    method: MethodInfo,
    input: object,
    options: RpcOptions,
  ): ServerStreamingCall {
    // we have to return an output stream before we get one from next()
    const outputStream = new RpcOutputStreamController()

    // next() creates a call instance, wait until we have a token
    const callPromise = new Promise<[ServerStreamingCall]>(
      (resolve, reject) => {
        this.callback()?.then(
          (response) => {
            let session: ActiveSession | EmptySession | ClientSession | null
            if (Array.isArray(response)) {
              ;[session] = response
            } else {
              session = response
            }

            if (!options.meta) options.meta = {}
            if (session?.accessToken)
              options.meta.Authorization = `Bearer ${session?.accessToken}`

            const call = next(method, input, options)

            // delegate
            call.responses.onNext((message, error, done) => {
              if (message) outputStream.notifyMessage(message)
              if (error) outputStream.notifyError(error)
              if (done) outputStream.notifyComplete()
            })

            // deliberately returning a tuple. the call is awaitable - the promise chain will wait for the call to finish
            resolve([call])
          },
          (error) => {
            let err: Error

            if (isUserError(error) || isSystemError(error)) {
              err = error
            } else if (isK2Error(error)) {
              err = error.toApplicationError()
            } else {
              err = makeErrorFromRpcError(error)
            }

            logger.error(err, { feature: logger.Feature.Auth })
            outputStream.notifyError(err)
            reject(err)
          },
        )
      },
    )

    // valid call needs to be returned immediately, wrap the promised call.
    return new ServerStreamingCall(
      method,
      options.meta ?? {},
      input,
      callPromise.then((c) => c[0].headers).catch((e) => e),
      outputStream,
      callPromise.then((c) => c[0].status).catch((e) => e),
      callPromise.then((c) => c[0].trailers).catch((e) => e),
    )
  }
}
